/**
 * Reconstructs a session's tool-call "steps" from its raw, persisted ADK
 * event log — the same shape WorkflowTimeline.jsx already renders LIVE (fed
 * by mia-chatbot-widget's onToolCall), but for a session that isn't open in
 * a live chat right now (History/Kanban's step drawer).
 *
 * Deliberately a hand-ported, simplified twin of
 * mia-chatbot-widget/src/hooks/useChat.ts's `extractTextFromEvents` tool-call
 * pairing logic (that package can't be imported across the host boundary —
 * same constraint its own utils/toolCall.ts notes) — walks
 * `event.content.parts[]` for `functionCall`/`functionResponse` pairs, keyed
 * by call id. Skips ADK's own `adk_request_confirmation` pause-and-ask
 * pseudo-calls (never a real tool the agent used) and the rewind-history
 * edge case (steer/rewind), which is rare enough for a historical steps
 * view to not need — a rewound step just shows up as an extra, already-done
 * step rather than being hidden, acceptable for this read-only summary.
 */

/**
 * @param {Array<object>} events raw ADK events (camelCase, as returned by
 *   AdkSessionsService.getAdkSession)
 * @returns {Array<{id: string, name: string, args: object, response: any, startedAt: number|undefined, endedAt: number|undefined}>}
 */
export function extractToolCallSteps(events) {
  if (!Array.isArray(events) || events.length === 0) return [];

  const byId = new Map();
  for (const event of events) {
    const parts = event.content?.parts || [];
    const at = typeof event.timestamp === 'number' ? event.timestamp * 1000 : undefined;
    for (const part of parts) {
      if (part.functionCall) {
        const { id, name, args } = part.functionCall;
        if (name === 'adk_request_confirmation') continue;
        if (!byId.has(id)) {
          byId.set(id, { id, name, args: args || {}, startedAt: at });
        }
      }
      if (part.functionResponse) {
        const { id, name, response } = part.functionResponse;
        const existing = byId.get(id);
        if (existing) {
          byId.set(id, { ...existing, endedAt: at, response });
        } else if (name !== 'adk_request_confirmation') {
          byId.set(id, { id, name, args: {}, startedAt: undefined, endedAt: at, response });
        }
      }
    }
  }
  // Preserve first-seen order (Map iteration order = insertion order).
  return Array.from(byId.values());
}

/**
 * `success`/`error` reading mirrors the widget's own `extractToolError`
 * heuristics (bare `error`, explicit `success: false`, MCP `isError`) —
 * kept intentionally small here since History only needs a status dot, not
 * a failure message.
 */
function stepFailed(response) {
  if (!response || typeof response !== 'object') return false;
  return response.success === false || response.isError === true || response.ok === false || Boolean(response.error);
}

/**
 * Buckets steps into todo/wip/done for the Kanban step drawer.
 * - `wip`: no response yet — only really meaningful while `isSessionActive`;
 *   for a closed/historical session this bucket should be empty in practice
 *   (every call that started, finished, one way or another) but a step
 *   still missing a response is shown here rather than silently dropped.
 * - `done`: has a response (success or error — `failed` flag lets the UI
 *   still tell them apart).
 * - `todo`: intentionally always empty — a plain chat session has no
 *   forward-looking plan to show ahead of time (unlike a workflow's own
 *   statically-defined step list). Kept as a real bucket (not omitted) so
 *   the UI's three-column layout stays uniform whether or not it's used.
 */
export function bucketToolCallSteps(steps) {
  const todo = [];
  const wip = [];
  const done = [];
  for (const step of steps) {
    if (step.response === undefined) {
      wip.push(step);
    } else {
      done.push({ ...step, failed: stepFailed(step.response) });
    }
  }
  return { todo, wip, done };
}
